import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, MenuItem, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import z from 'zod';
import { IChatbotFlowModel } from '../../../data/models/chatbot';
import { IFlowModel } from '../../../data/models/flow';
import { FlowService } from '../../../service/FlowService';
import { GroqService } from '../../../service/GroqService';
import { useNotificationStore } from '../../../store/storeNotifications';
import { MuiSelectControlled } from '../../Form/Select';
import { MuiSwitchControlled } from '../../Form/Switch';

const AIVoiceReadSettingsSchema = z
  .object({
    chatbotVoiceReadEnabled: z.boolean().default(false),
    chatbotVoiceModel: z.string().optional(),
    chatbotVoice: z.string().optional(),
  })
  .refine(
    data => {
      if (data.chatbotVoiceReadEnabled) {
        return !!data.chatbotVoiceModel && !!data.chatbotVoice;
      }

      return true;
    },
    {
      message: 'Required',
      path: ['chatbotVoiceModel', 'chatbotVoice'],
    },
  );

export type AIVoiceReadSettings = z.infer<typeof AIVoiceReadSettingsSchema>;

export function AIVoiceReadSettings({
  chatData,
  onSetFlow,
}: {
  chatData: IChatbotFlowModel;
  onSetFlow: (data: any) => void;
}) {
  const [groqVoices, setGroqVoices] = useState<{ id: string; name: string }[]>(
    [],
  );
  const [flow, setFlow] = useState<IFlowModel>({} as IFlowModel);
  const setNotification = useNotificationStore(state => state.setNotification);

  const formsMethods = useForm<AIVoiceReadSettings>({
    defaultValues: {
      chatbotVoiceReadEnabled: false,
    },
    resolver: zodResolver(AIVoiceReadSettingsSchema),
  });

  const chatbotVoiceReadEnabled = formsMethods.watch('chatbotVoiceReadEnabled');
  const selectedVoiceModel = formsMethods.watch('chatbotVoiceModel');

  function renderVoices() {
    if (selectedVoiceModel === 'groq') {
      return groqVoices.map(voice => (
        <MenuItem key={voice.id} value={voice.id}>
          {voice.name}
        </MenuItem>
      ));
    }

    return null;
  }

  async function handleSubmitForms(data: AIVoiceReadSettings) {
    try {
      const response = await FlowService.updateFlow(chatData.id, {
        chatbotVoiceModel: data.chatbotVoiceReadEnabled
          ? data.chatbotVoiceModel
          : null,
        chatbotVoice: data.chatbotVoiceReadEnabled ? data.chatbotVoice : null,
      });

      onSetFlow(response.data);

      setNotification({
        type: 'success',
        message: 'Saved successfully',
      });
    } catch (error) {
      console.error(error);
      setNotification({
        type: 'error',
        message: 'Error saving AI voice read settings',
      });
    }
  }

  useEffect(() => {
    async function getVoices() {
      const groqVoices = await GroqService.voices();
      setGroqVoices(groqVoices.data.voices);
    }

    getVoices();
  }, []);

  useEffect(() => {
    async function getFlow() {
      const flow = await FlowService.getFlow(chatData.id);
      setFlow(flow.data);
    }

    getFlow();
  }, [chatData.id]);

  useEffect(() => {
    if (chatData) {
      formsMethods.reset(
        {
          chatbotVoiceReadEnabled: !!chatData.chatbotVoiceModel,
          chatbotVoiceModel: chatData.chatbotVoiceModel ?? '',
          chatbotVoice: chatData.chatbotVoice ?? '',
        },
        { keepDefaultValues: true },
      );
    }
  }, [chatData, formsMethods, flow.nodes]);

  return (
    <FormProvider {...formsMethods}>
      <form
        id="ai-voice-read-settings"
        onSubmit={formsMethods.handleSubmit(handleSubmitForms)}
        style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
      >
        <Box
          width="100%"
          display="flex"
          gap="8px"
          alignItems="start"
          flexDirection="column"
        >
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: '500',
              color: '#101828',
            }}
          >
            AI Voice Read
          </Typography>

          <MuiSwitchControlled
            switchProps={{
              name: 'chatbotVoiceReadEnabled',
              label: `Enable AI Voice Read mode`,
            }}
          />

          {chatbotVoiceReadEnabled && (
            <Box width="100%" display="flex" gap="8px" alignItems="baseline">
              <MuiSelectControlled
                styles={{ width: '200px' }}
                selectProps={{
                  name: 'chatbotVoiceModel',
                  label: 'AI Voice Model',
                  placeholder: 'Select AI Voice Model',
                }}
              >
                <MenuItem value="none" disabled>
                  Select a voice model
                </MenuItem>
                <MenuItem value="groq">PlayAi</MenuItem>
              </MuiSelectControlled>

              <MuiSelectControlled
                styles={{ width: '200px' }}
                selectProps={{
                  name: 'chatbotVoice',
                  label: 'AI Voice',
                  placeholder: 'Select AI Voice',
                }}
              >
                <MenuItem value="none" disabled>
                  Select a voice
                </MenuItem>
                {renderVoices()}
              </MuiSelectControlled>
            </Box>
          )}
        </Box>
      </form>

      <Box display="flex" justifyContent="end" width="100%">
        <Button
          form="ai-voice-read-settings"
          type="submit"
          variant="contained"
          sx={{ width: '160px' }}
        >
          Save changes
        </Button>
      </Box>
    </FormProvider>
  );
}
